/**
 * THE APPLICATION PALETTE'S PROVIDER LIST, COMPOSED (#2328 §7, ruling 74071 §5).
 *
 * #2328 §7: *"Its provider graph includes business navigation, permitted records,
 * and normal commands only. It must not import, receive, query, rank, or display
 * the Settings registry … This exclusion occurs at provider/registry composition
 * time, not as a string filter after results are returned."*
 *
 * ── THE SEAM DID NOT EXIST, AND THAT IS WHY THE SENTENCE WAS UNPROVABLE ────
 * Before this file, `CommandPalette.tsx` was one inlined `useMemo` with a
 * twenty-odd-entry dependency array. There was no list of providers, so there was
 * nothing for an exclusion to happen *to* — the only way to write the property
 * down would have been "the component does not mention settings", which is a
 * statement about a file rather than about a composition. This module is the
 * list, and `providers.test.ts` asserts over it.
 *
 * ── TWO EXCLUSIONS, AND THEY ARE DIFFERENT CLAIMS ──────────────────────────
 *
 *  1 · THE SETTINGS REGISTRY IS EXCLUDED **BY CONSTRUCTION**, not by consequence.
 *      Nothing in this module's transitive import graph reaches
 *      `apps/console/src/settings/` — asserted in both directions by
 *      `imports.census.test.ts`. There is no settings provider to filter out,
 *      no `Open Settings` result to suppress, and no flag that turns settings
 *      results on. B4 removed Settings from business navigation, which makes the
 *      palette clean as a CONSEQUENCE; this file makes it clean by CONSTRUCTION,
 *      and the ruling asks for the second. The distinction is not pedantic: a
 *      consequence is restored the moment somebody adds a settings route back to
 *      a nav definition, and nothing would go red.
 *
 *  2 · ADMINISTRATIVE SUBJECTS ARE EXCLUDED **BEFORE THE QUERY**, not after it.
 *      Ruling §5 is wider than the registry: administrative ENTITIES AND THEIR
 *      RECORDS must not be *queried*. `searchTools` below is the list handed to
 *      `runFederatedSearch`, and an administrative door never enters it — so the
 *      fan-out never asks. `navigation`, `recents` and `pins` are filtered the
 *      same way and for the same reason: a recent record of an administrative
 *      entity is *its record*, which the ruling names explicitly.
 *
 * ── THE EXCLUSIONS ARE RETURNED, NOT DISCARDED ─────────────────────────────
 * `excluded` carries every subject left out with the classification that left it
 * out. It exists because a filter that returns only its survivors cannot be
 * distinguished from a filter that never ran — the classic vacuous green (skill
 * rule 25). A test can assert that the administrative doors were *considered and
 * refused*, which is the actual property, rather than that they are *absent*,
 * which an empty tool list satisfies.
 *
 * It is deliberately NOT rendered anywhere. #2328 §10's permission-boundary
 * journey forbids leaking what is being withheld, and while these exclusions are
 * classification rather than authority, drawing "3 sources hidden" in the footer
 * would be the same habit one step away from the case where it matters.
 *
 * ── WHAT THIS MODULE MAY IMPORT ────────────────────────────────────────────
 * `federatedSearch` (the tool shape), `@solum/experience` (the nav shape),
 * `paletteClass` and `consoleLauncherActions` (the launcher's backstage
 * destinations — pure, a type-only kit import). No React, no router, no query
 * client, and above all nothing under `../../settings/`. Keeping it a pure function of its inputs is what lets
 * the composition be asserted without mounting anything.
 *
 * ── MUTATION (skill rule 10) — the table is in `providers.test.ts` ─────────
 */
import type { NavGroup } from '@solum/experience'
import type { SearchTool } from '../federatedSearch'
import { BACKSTAGE_DESTINATIONS } from '../consoleLauncherActions'
import {
  classifyPaletteSubject, type PaletteClass, type PaletteClassification, type PaletteSubjectKind,
} from './paletteClass'

/**
 * A record reference the palette holds before it knows anything else about it —
 * the shape `recents.ts` stores and `pins.ts` carries for a record pin.
 */
export interface PaletteRecordRef {
  entity: string
  id: string
}

/** A pin, as `usePins` returns it, narrowed to what classification needs. */
export interface PalettePin {
  kind: string
  entity?: string | null
}

/** One subject the composition refused, with the classification that refused it. */
export interface ExcludedSubject {
  kind: PaletteSubjectKind
  key: string
  /** Which provider it would have fed. */
  provider: ApplicationProviderId
  classification: PaletteClassification
}

/**
 * The providers the ordinary palette composes, named.
 *
 * A closed union rather than free strings: `providers.test.ts` asserts the
 * composed set EQUALS this vocabulary, so a provider added without a
 * classification decision is a type error at the point it is added rather than a
 * silent new result group.
 */
export const APPLICATION_PROVIDERS = [
  'pinned',
  'recent',
  'federated-search',
  'record-pool',
  'entity-catalogue',
  'product-destination',
  'backstage-destination',
  'scope',
  'do',
  'ask',
] as const

export type ApplicationProviderId = typeof APPLICATION_PROVIDERS[number]

/**
 * Generic over the pin and record shapes on purpose.
 *
 * The composition only ever READS `entity` off them; narrowing the caller's
 * `PinnedItem` down to the two fields classification needs would force the
 * palette to re-widen it afterwards, and a cast at that seam is where a filtered
 * list quietly becomes the unfiltered one again.
 */
export interface ApplicationProviderInput<P extends PalettePin, R extends PaletteRecordRef> {
  /** Every read-only search tool `describe_tools()` offered. */
  tools: readonly SearchTool[]
  /** The entity catalogue, as `buildNav` composed it. */
  navigation: readonly NavGroup[]
  /** The caller's recent records, resolved or not. */
  recents: readonly R[]
  /** The caller's pins. */
  pins: readonly P[]
  /**
   * The governed classification of one entity, when the deployment compiled one.
   *
   * A function rather than a map so the caller keeps ownership of where the
   * answer comes from — today `describe_schema()`'s rows, tomorrow the same rows
   * carrying `palette_class`. Returning `undefined` means "this deployment does
   * not answer", which is not the same as "business" (see `paletteClass.ts`).
   */
  governedEntityClass?: (entity: string) => PaletteClass | null | undefined
  /** The same, for a registered search tool. */
  governedToolClass?: (tool: string) => PaletteClass | null | undefined
  /**
   * The launcher's backstage gate, answered for this caller
   * (`backstageAdmitted`, ROOT c.116770 G-A). Absent ⇒ closed: a caller that
   * does not say is offered no backstage destination.
   */
  backstage?: boolean
}

export interface ApplicationProviders<P extends PalettePin, R extends PaletteRecordRef> {
  /** The providers this composition offers. Always the full vocabulary. */
  providers: readonly ApplicationProviderId[]
  /** The tools the federated fan-out may ASK. Administrative doors are absent. */
  searchTools: readonly SearchTool[]
  /** The entity catalogue, minus administrative entities. */
  navigation: readonly NavGroup[]
  recents: readonly R[]
  pins: readonly P[]
  /**
   * The gated backstage DESTINATIONS (People & Access) — places to go, offered
   * only under the launcher's backstage gate. Never a search: what they
   * administer stays in `excluded`.
   */
  destinations: readonly { to: string; label: string }[]
  /** Everything refused, with the reason. See the header. */
  excluded: readonly ExcludedSubject[]
}

/**
 * Compose the ordinary application palette's providers.
 *
 * Pure, synchronous and total. It performs no IO — which is the point: the
 * exclusion has to be decidable *before* anything is asked, so the thing that
 * decides it cannot be the thing that asks.
 */
export function applicationProviders<P extends PalettePin, R extends PaletteRecordRef>(
  input: ApplicationProviderInput<P, R>,
): ApplicationProviders<P, R> {
  const excluded: ExcludedSubject[] = []

  const admits = (
    kind: PaletteSubjectKind, key: string, provider: ApplicationProviderId,
    governed: PaletteClass | null | undefined,
  ): boolean => {
    const classification = classifyPaletteSubject({ kind, key, governed })
    if (classification.paletteClass === 'administrative') {
      excluded.push({ kind, key, provider, classification })
      return false
    }
    return true
  }

  /* THE FAN-OUT LIST. This is the line ruling §5 is about: a tool that is not
     here is never passed to `runFederatedSearch`, so its door is never called,
     so it contributes nothing to the latency, nothing to the source count and
     nothing to the server's log. Classification happens on the tool NAME, which
     `core.register_mcp_tool` guarantees globally unique, and on the entity the
     tool resolves to — either being administrative is enough, because a business
     door onto an administrative entity is still a read of that entity. */
  const searchTools = input.tools.filter((tool) => {
    if (!admits('search-door', tool.name, 'federated-search', input.governedToolClass?.(tool.name))) return false
    if (tool.entity === null) return true
    return admits('entity', tool.entity, 'federated-search', input.governedEntityClass?.(tool.entity))
  })

  /* THE ENTITY CATALOGUE. `buildNav`'s groups are how `/e/$entity` destinations
     reach the palette, and they are the second half of the census finding: typing
     `persona` or `role` returned an administrative list page. Groups that end up
     empty are dropped rather than rendered as a heading over nothing. */
  const navigation = input.navigation
    .map((group) => ({
      ...group,
      entities: group.entities.filter((entity) =>
        admits('entity', entity.key, 'entity-catalogue', input.governedEntityClass?.(entity.key))),
    }))
    .filter((group) => group.entities.length > 0)

  /* RECORDS OF AN ADMINISTRATIVE ENTITY. Ruling §5 says "entities AND THEIR
     RECORDS", and a recent or a pin is precisely a record the palette would
     offer without anybody searching for it. */
  const recents = input.recents.filter((ref) =>
    admits('entity', ref.entity, 'recent', input.governedEntityClass?.(ref.entity)))

  const pins = input.pins.filter((pin) => {
    const entity = pin.entity
    if (pin.kind !== 'record' || entity === undefined || entity === null) return true
    return admits('entity', entity, 'pinned', input.governedEntityClass?.(entity))
  })

  /* A GATED DESTINATION, NOT ADMINISTRATIVE SEARCH (ROOT c.116770 G-A, which
     supersedes the destination exclusion for this one route). People & Access
     is reachable from ⌘K exactly where the launcher's Platform settings tile
     is — the same predicate, no second permission — while every subject it
     administers is still refused above, before anything is asked. */
  const destinations = input.backstage === true ? BACKSTAGE_DESTINATIONS : []

  return { providers: APPLICATION_PROVIDERS, searchTools, navigation, recents, pins, destinations, excluded }
}
